import React from "react";
import { Dispatch } from "@reduxjs/toolkit";
import { Location, NavigateFunction } from "react-router-dom";
import { Flex, Stack } from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import {
  ConferenceConfiguration,
  Currency,
  Language,
} from "../../types/conference.types";
import { ParticipantFeeSummary } from "../../types/fee.types";
import { RegistrationRequest } from "../../types/registration.types";
import { isInsideIframe } from "../../utils/iframe-utils";
import { useConferenceQuery } from "../../hooks/useConferenceQuery";
import { useAccomodationsQuery } from "../../hooks/useAccomodationsQuery";
import { usePreviewForm } from "../../hooks/usePreviewForm";
import { usePreviewSideEffects } from "../../hooks/usePreviewSideEffects";
import { useGetHeightAndSendMessage } from "../../utils/useGetHeightAndSendMessage";
import CurrencyContext from "../../context/CurrencyContext";
import Blur from "../common/Blur";
import { GreenButton } from "../common/CongressButton";
import { PageSkeleton } from "../common/PageSkeleton";
import { PreviewFormSections } from "./PreviewFormSections";
import { useModuleChanges } from "../../hooks/useModuleChanges";
import { useEditParticipantContext } from "../../context/EditParticipantContext";

interface PreviewConTentProps {
  i18n: { activate: Function };
  congressId: number;
  dispatch: Dispatch;
  location: Location;
  navigate: NavigateFunction;
  initialValues?: RegistrationRequest;
  isEditMode?: boolean;
  formikRef?: React.RefObject<{ submitForm: () => void }>;
  registrationConfigJson?: ConferenceConfiguration;
  /** Szerkesztés módban: résztvevő eredeti kísérői díj összefoglalója (`useModuleChanges` escort ághoz). */
  participantEscortFee?: ParticipantFeeSummary | null;
  onEditSubmitSuccess?: (values: RegistrationRequest) => void;
  showBlurs?: boolean;
}

const PreviewContent: React.FC<PreviewConTentProps> = ({
  i18n,
  congressId,
  dispatch,
  location,
  navigate,
  initialValues,
  isEditMode = false,
  formikRef,
  registrationConfigJson,
  participantEscortFee,
  onEditSubmitSuccess,
  showBlurs = true,
}) => {
  const editCtx = useEditParticipantContext();
  const pageRef = useGetHeightAndSendMessage();
  const { congressEvent, isLoadingCongressEvent, isError } = useConferenceQuery(
    congressId,
    isEditMode,
  );

  const { accomodations, isLoadingAccomodations } = useAccomodationsQuery(
    congressId,
    isEditMode,
    congressEvent,
  );

  const [conditionsAccepted, setConditionsAccepted] =
    React.useState<boolean>(false);

  const language = React.useMemo(
    () => congressEvent?.language ?? Language.HUNGARIAN,
    [congressEvent],
  );

  const currency = React.useMemo(() => {
    return congressEvent?.currency === Currency.HUF
      ? congressEvent?.language === Language.HUNGARIAN
        ? "Ft"
        : "HUF"
      : "EUR";
  }, [congressEvent]);

  const isPreviewPage = React.useMemo(
    () => location.pathname.includes("/congress"),
    [location.pathname],
  );
  const isIframe = React.useMemo(() => isInsideIframe(), []);

  const goBack = React.useCallback(() => {
    i18n.activate("hu");
    navigate(-1);
  }, [navigate, i18n]);

  const { formik, rooms, onCopy, registerState } = usePreviewForm({
    congressEvent,
    congressId,
    initialValues,
    isEditMode,
    formikRef,
    registrationConfigJson,
    onEditSubmitSuccess,
  });

  const moduleChanges = useModuleChanges(
    congressId,
    registrationConfigJson ?? null,
    isEditMode,
    participantEscortFee,
  );

  usePreviewSideEffects({
    i18n,
    congressEvent,
    isPreviewPage,
    isError,
    navigate,
    dispatch,
    isSuccessRegistration: registerState.isSuccess,
    registrationResult: registerState.data,
  });

  return (
    <>
      <CurrencyContext.Provider value={currency}>
        <Flex
          minH={"100%"}
          align={"center"}
          bg={"transparent"}
          direction="column"
          my="12"
          ref={pageRef}
          {...(isEditMode && { width: "100%" })}
        >
          <Stack
            spacing={4}
            w={"full"}
            bg={"transparent"}
            rounded={"xl"}
            maxW={"4xl"}
            boxShadow={"lg"}
            p={6}
          >
            <form
              ref={
                isEditMode && editCtx?.formRef
                  ? (editCtx.formRef as React.RefObject<HTMLFormElement>)
                  : undefined
              }
              onSubmit={formik.handleSubmit}
              style={{ width: "100%" }}
            >
              {congressEvent && !isLoadingCongressEvent ? (
                <PreviewFormSections
                  congressEvent={congressEvent}
                  congressId={congressId}
                  formik={formik}
                  isEditMode={isEditMode}
                  accomodations={accomodations}
                  rooms={rooms}
                  isLoadingAccomodations={isLoadingAccomodations}
                  onCopy={onCopy}
                  conditionsAccepted={conditionsAccepted}
                  onChangeConditions={setConditionsAccepted}
                  isRegistering={registerState.isLoading}
                  language={language}
                  moduleChanges={moduleChanges}
                />
              ) : (
                <PageSkeleton />
              )}
            </form>
          </Stack>
        </Flex>
        {!isPreviewPage && !isIframe && !isEditMode && (
          <GreenButton
            onClick={goBack}
            style={{
              position: "fixed",
              top: 16,
              right: "10%",
            }}
            testId="preview-back-button"
          >
            <Trans>Back</Trans>
          </GreenButton>
        )}
        {!isIframe && showBlurs && (
          <>
            <Blur
              position={"sticky"}
              bottom={-50}
              left={"90%"}
              transform={"rotate(180deg)"}
              style={{ filter: "blur(70px)" }}
              zIndex={-1}
            />
            <Blur
              position={"sticky"}
              bottom={"100%"}
              left={"-20%"}
              marginLeft={"-32%"}
              marginTop={"-7%"}
              style={{ filter: "blur(70px)" }}
              zIndex={-1}
            />
          </>
        )}
      </CurrencyContext.Provider>
    </>
  );
};

export default React.memo(PreviewContent);
